import {
  Box,
  createColumnHelper,
  IconButton,
  Label,
  Table,
  useResponsiveValue,
} from 'ui'
import { useCompanyMembersContext } from '../context/CompanyMembersProvider'
import { AllCompaniesMembersResponse } from 'api/company'
import { toTitleCase } from 'lib/string'
import { useAuthContext } from 'components/auth'
import { Pagination } from './Pagination'
import { PencilIcon } from 'lucide-react'

const AccessToCurrentCompanyCell = ({
  member,
}: {
  member: AllCompaniesMembersResponse['members'][number]
}) => {
  const { company } = useAuthContext()

  const memberHasAccessToCurrentCompany = member.companies.some(
    (memberCompany) => memberCompany.id === company?.id
  )

  if (!memberHasAccessToCurrentCompany) {
    return (
      <Label variant="neutral" size="small">
        No access
      </Label>
    )
  }

  return (
    <Label variant="success" size="small">
      Has access
    </Label>
  )
}

const memberColumnHelper =
  createColumnHelper<AllCompaniesMembersResponse['members'][number]>()

export const CompanyMembersTable = () => {
  const tableMode = useResponsiveValue(['borderless', 'standard']) as
    | 'borderless'
    | 'standard'

  const { company } = useAuthContext()
  const {
    state: { membersPage, isInCompanyGroup },
    actions: { setSelectedMemberId },
  } = useCompanyMembersContext()

  const columns = [
    memberColumnHelper.accessor('name', {
      header: 'Name',
      cell: (info) => toTitleCase(info.getValue()),
    }),
    memberColumnHelper.accessor('email', {
      header: 'Email',
    }),
    memberColumnHelper.accessor('companyRolePermission.description', {
      header: 'Role',
    }),
    ...(isInCompanyGroup
      ? [
          memberColumnHelper.accessor('companies', {
            header: `Access to ${company?.name}`,
            cell: (info) => (
              <AccessToCurrentCompanyCell member={info.row.original} />
            ),
          }),
        ]
      : []),
    memberColumnHelper.accessor('id', {
      header: '',
      size: 30,
      cell: (info) => (
        <IconButton onClick={() => setSelectedMemberId(info.row.original.id)}>
          <PencilIcon size={16} />
        </IconButton>
      ),
    }),
  ]

  return (
    <Box>
      <Table variant={tableMode} columns={columns} data={membersPage} />
      <Pagination />
    </Box>
  )
}
